import type { LucideIcon } from 'lucide-react';
import { matchPath } from 'react-router';
import { hasAnyPermission, hasPermission } from '@helios/authz';
import {
  ALL_DESTINATIONS,
  findDestinationById,
  userCanAccessDestination,
} from '@/navigation/registry';
import type { SubjectHub, SubjectHubPhase, SubjectHubTab } from '@/navigation/hubs';

export interface ResolvedHubTab {
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
  destinationId?: string;
  permission?: string;
  anyPermissions?: readonly string[];
  activePaths?: readonly string[];
  fallbackDestinationId?: string;
}

export function resolveHubTabs(hub: SubjectHub): ResolvedHubTab[] {
  return hub.tabs.map((tab, index) => resolveHubTab(hub, tab, index));
}

function resolveHubTab(hub: SubjectHub, tab: SubjectHubTab, index: number): ResolvedHubTab {
  if (tab.destinationId) {
    const destination = findDestinationById(tab.destinationId);
    if (!destination) {
      throw new Error(
        `Subject hub "${hub.id}" references unknown destination: ${tab.destinationId}`,
      );
    }
    return {
      to: tab.to ?? destination.path,
      label: tab.label ?? destination.label,
      icon: tab.icon ?? destination.icon,
      end: tab.end ?? true,
      destinationId: tab.destinationId,
      activePaths: tab.activePaths,
      fallbackDestinationId: tab.fallbackDestinationId,
    };
  }
  if (!tab.to || !tab.label || !tab.icon) {
    throw new Error(`Subject hub "${hub.id}" tab ${index} is missing to/label/icon`);
  }
  return {
    to: tab.to,
    label: tab.label,
    icon: tab.icon,
    end: tab.end ?? true,
    permission: tab.permission,
    anyPermissions: tab.anyPermissions,
    activePaths: tab.activePaths,
  };
}

export function filterHubTabsForPermissions(
  tabs: readonly ResolvedHubTab[],
  permissions: readonly string[] | null,
): ResolvedHubTab[] {
  if (permissions === null) {
    return [...tabs];
  }
  return tabs.flatMap((tab) => {
    if (tab.destinationId) {
      const destination = findDestinationById(tab.destinationId);
      if (destination && userCanAccessDestination(destination, permissions)) return [tab];
      const fallback = tab.fallbackDestinationId
        ? findDestinationById(tab.fallbackDestinationId)
        : undefined;
      return fallback && userCanAccessDestination(fallback, permissions)
        ? [{ ...tab, to: fallback.path }]
        : [];
    }
    return tab.permission &&
      hasPermission(permissions, tab.permission) &&
      (!tab.anyPermissions?.length || hasAnyPermission(permissions, tab.anyPermissions))
      ? [tab]
      : [];
  });
}

/**
 * True when the path is exactly a catalog destination (including `:param`
 * patterns) that these permissions may open. A hub must not redirect such a
 * screen to its first tab just because the owning tab needs another grant,
 * e.g. a client contact record opened by a contact-only reader.
 */
export function isAccessibleDestinationPath(
  pathname: string,
  permissions: readonly string[],
): boolean {
  return ALL_DESTINATIONS.some(
    (destination) =>
      matchPath({ path: destination.path, end: true }, pathname) !== null &&
      userCanAccessDestination(destination, permissions),
  );
}

function pathHitsPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || (prefix !== '/' && pathname.startsWith(`${prefix}/`));
}

export function pathMatchesTab(
  pathname: string,
  tab: Pick<ResolvedHubTab, 'to' | 'end' | 'activePaths'>,
): boolean {
  if (pathname === tab.to) return true;
  if (!tab.end && pathHitsPrefix(pathname, tab.to)) return true;
  return (tab.activePaths ?? []).some((prefix) => pathHitsPrefix(pathname, prefix));
}

export function findActiveHubTab(
  pathname: string,
  tabs: readonly ResolvedHubTab[],
): ResolvedHubTab | undefined {
  let best: { tab: ResolvedHubTab; length: number } | undefined;
  for (const tab of tabs) {
    const prefixes = [tab.to, ...(tab.activePaths ?? [])];
    for (const prefix of prefixes) {
      if (!pathHitsPrefix(pathname, prefix)) continue;
      if (!best || prefix.length > best.length) {
        best = { tab, length: prefix.length };
      }
    }
  }
  return best?.tab;
}

export interface ResolvedHubPhase {
  id: string;
  label: string;
  icon: LucideIcon;
  step: number;
  to: string;
  tabLabels: readonly string[];
  destinationIds: readonly string[];
}

export function resolveHubPhases(
  phases: readonly SubjectHubPhase[] | undefined,
  tabs: readonly ResolvedHubTab[],
): ResolvedHubPhase[] {
  if (!phases?.length) return [];
  const tabsByDestination = new Map(
    tabs.filter((tab) => tab.destinationId).map((tab) => [tab.destinationId as string, tab]),
  );
  const resolved: ResolvedHubPhase[] = [];
  for (const phase of phases) {
    const phaseTabs = phase.destinationIds
      .map((id) => tabsByDestination.get(id))
      .filter((tab): tab is ResolvedHubTab => Boolean(tab));
    const first = phaseTabs[0];
    if (!first) continue;
    resolved.push({
      id: phase.id,
      label: phase.label,
      icon: phase.icon,
      step: resolved.length + 1,
      to: first.to,
      tabLabels: phaseTabs.map((tab) => tab.label),
      destinationIds: phaseTabs
        .map((tab) => tab.destinationId)
        .filter((id): id is string => Boolean(id)),
    });
  }
  return resolved;
}

export function findActiveHubPhase(
  activeTab: ResolvedHubTab | undefined,
  phases: readonly ResolvedHubPhase[],
): ResolvedHubPhase | undefined {
  if (!activeTab?.destinationId) return undefined;
  return phases.find((phase) => phase.destinationIds.includes(activeTab.destinationId as string));
}
